import React, { useState, useCallback, useEffect } from 'react'
import { Box, Button, Divider, Grid, IconButton, TextField, Typography, Tooltip, Chip, Stack } from "@mui/material";
import InputField from "../FormFields/InputField";
import SelectField from "../FormFields/SelectField";
import RadioGroupField from "../FormFields/RadioGroupField";
import CheckboxField from "../FormFields/CheckboxField";
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CheckboxGroupField from "../FormFields/CheckboxGroupField";
import SliderField from "../FormFields/SliderField";
import DynamicFormField from './DynamicFormField';
import { getIn } from "formik";
import { useResponsive } from '../../../hooks/ResponsiveProvider';
import { checkFieldVisibility } from '../utils/dependsOnValidationService';
import QuizIcon from '@mui/icons-material/Quiz';
import { getStatusColor } from '../../util';
import { useSearchParams } from 'react-router-dom';
import CommentHistory from '../../subjectDetails/commentHistory/CommentHistory';


const DynamicFields = (props) => {
  const { isSmallScreen } = useResponsive();
  const {
    _config,
    parentKey,
    setFieldValue,
    values,
    touched,
    errors,
    readOnly,
    siteInitiationMasterId,
    userSiteTrialRole,
    queriesByFieldKey,
    sectionKey,
    subSectionKey,
    stepStatusData,
    formConfig,
  } = props;
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [openQueryDrawer, setOpenQueryDrawer] = useState(false);
  const [selectedQueryField, setSelectedQueryField] = useState(null);
  
  // Handle opening the query drawer for a field
  const handleOpenQueryDrawer = useCallback((field) => {
    setSelectedQueryField(field);
    setOpenQueryDrawer(true);
    
    // Update URL query params
    const params = new URLSearchParams(searchParams);
    params.set('fieldKey', field.key);
    params.set('tab', 'query');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  
  // Handle closing the query drawer
  const handleCloseQueryDrawer = useCallback(() => {
    setOpenQueryDrawer(false);
    setSelectedQueryField(null);
    
    // Remove fieldKey and tab from URL
    const params = new URLSearchParams(searchParams);
    params.delete('fieldKey');
    params.delete('tab');
    params.delete('queryId');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Check URL params on mount/update to open drawer if fieldKey is present
  useEffect(() => {
    const fieldKeyParam = searchParams.get('fieldKey');
    if (fieldKeyParam && _config) {
      const field = _config.find(f => f.key === fieldKeyParam);
      if (field && !openQueryDrawer) {
        setSelectedQueryField(field);
        setOpenQueryDrawer(true);
      }
    }
  }, [searchParams, _config, openQueryDrawer]);
    
    // Helper function to check if user has access to a field
    const hasFieldAccess = (field) => {
        if (!field.access) return true;
        return field.access === userSiteTrialRole || field.access.includes(userSiteTrialRole);
    };
    
    // Helper function to determine if field should be rendered
    // Signature fields are always rendered (but may be disabled), other fields require access
    const shouldRenderField = (field) => {
        if (!checkFieldVisibility(field, values)) return false;
        // Signature fields are always rendered (shown as disabled if no access)
        if (field.type === 'SIGNATURE') return true;
        // Other fields require access
        return hasFieldAccess(field);
    };
    
  // Helper function to get queries for a field
  const getFieldQueries = (fieldKey) => {
    return queriesByFieldKey?.[fieldKey] || [];
  };

  // Helper function to get open queries count for a field
  const getOpenQueriesCount = (fieldKey) => {
    const fieldQueries = getFieldQueries(fieldKey);
    return fieldQueries.filter((q) => q.status === 'OPEN' || q.status === 'REOPENED').length;
  };

  // Helper function to check if field has any queries
  const fieldHasQueries = (fieldKey) => {
    return getFieldQueries(fieldKey).length > 0;
  };

  // Get query status counts for a field
  const getQueryStatusCounts = (fieldKey) => {
    const fieldQueries = getFieldQueries(fieldKey);
    if (!fieldQueries || fieldQueries.length === 0) return { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };

    const counts = { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
    fieldQueries.forEach((query) => {
      if (counts.hasOwnProperty(query.status)) {
        counts[query.status]++;
      }
      counts.total++;
    });
    return counts;
  };

  // Get status label for display
  const getStatusLabel = (status) => {
    switch (status) {
      case 'OPEN':
        return 'Open';
      case 'RESOLVED':
        return 'Resolved';
      case 'VERIFIED':
        return 'Verified';
      case 'REOPENED':
        return 'Reopened';
      default:
        return status;
    }
  };

  // Render query indicator for a field
  const renderQueryIndicator = (field) => {
    const statusCounts = getQueryStatusCounts(field.key);
    if (statusCounts.total === 0) return null;
    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Queries ({statusCounts.total}) - Click to view
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {['OPEN', 'REOPENED', 'RESOLVED', 'VERIFIED'].map(
                (status) =>
                  statusCounts[status] > 0 && (
                    <Chip
                      key={status}
                      label={`${getStatusLabel(status)}: ${statusCounts[status]}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        backgroundColor: getStatusColor(status).bg,
                        color: getStatusColor(status).text,
                        '& .MuiChip-label': {
                          paddingX: 0.75,
                        },
                      }}
                    />
                  )
              )}
            </Stack>
          </Box>
        }
        arrow
        placement="top"
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpenQueryDrawer(field);
          }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <QuizIcon
              sx={{
                fontSize: 18,
                color:
                  statusCounts.OPEN > 0 || statusCounts.REOPENED > 0
                    ? getStatusColor('OPEN').text
                    : statusCounts.RESOLVED > 0
                    ? getStatusColor('RESOLVED').text
                    : getStatusColor('VERIFIED').text,
              }}
            />
            {(statusCounts.OPEN > 0 || statusCounts.REOPENED > 0) && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: getStatusColor('OPEN').text,
                }}
              >
                {statusCounts.OPEN + statusCounts.REOPENED}
              </Typography>
            )}
          </Box>
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <>
        <Box>
        <Grid container spacing={isSmallScreen ? 0 : 1} padding={isSmallScreen ? 1 : 3}>
          {_config &&
            _config.map((field, fieldIndex) => {
              const openQueriesCount = getOpenQueriesCount(field.key);
              const hasQueries = fieldHasQueries(field.key);

              return field.key == 'divider' ? (
                <Grid key={`${field.key}${fieldIndex}`} item md={12} lg={12} sm={12} xs={12} sx={{ marginBottom: 2 }}>
                  <Box width={'100%'}>
                    <Divider />
                  </Box>
                </Grid>
              ) : (
                shouldRenderField(field) && (
                  <Grid
                    key={parentKey ? `${parentKey}.${fieldIndex}` : `aa${fieldIndex}`}
                    item
                    md={field.md}
                    lg={field.lg}
                    sm={field.sm}
                    xs={field.sm}
                    paddingY={1}
                    paddingX={isSmallScreen ? 1 : 3}
                    sx={{
                      position: 'relative',
                      // Highlight fields with open queries
                    //   backgroundColor: openQueriesCount > 0 ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                      backgroundColor: hasQueries ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                      borderRadius: hasQueries ? 1 : 0,
                      border: openQueriesCount > 0 ? `1px solid ${getStatusColor('OPEN').bg}` : 'none',
                    }}
                  >
                    {hasQueries && renderQueryIndicator(field)}
                    <DynamicFormField
                      {...props}
                      field={field}
                      hasSignatureAccess={hasFieldAccess(field)}
                      fieldQueries={getFieldQueries(field.key)}
                    />
                        </Grid>
                )
              );
            })}
            </Grid>
        </Box>
      
      {/* Query Detail Drawer */}
      {openQueryDrawer && selectedQueryField && (
        <CommentHistory
          open={openQueryDrawer}
          onCloseHistory={handleCloseQueryDrawer}
          field={selectedQueryField}
          answer={values?.[selectedQueryField.key]}
          sectionKey={sectionKey}
          subSectionKey={subSectionKey}
          categoryKey={parentKey}
          stepKey={stepStatusData?.stepKey}
          userSiteTrialRole={userSiteTrialRole}
          formConfig={formConfig}
          MASK_FIELD_KEY={[]}
        />
      )}
    </>
  );
}

export default React.memo(DynamicFields);